import type { Tenant } from "@/types/index"

/**
 * Escopo da unidade: qual unidade a pessoa está vendo.
 *
 * 🚨 A INVARIANTE. Com uma unidade visível só, o escopo é sempre essa unidade.
 * "Todas as unidades" existe apenas para quem tem duas ou mais.
 *
 * POR QUE EXISTE. O escopo nascia em "Todas as unidades" para todo mundo, e a
 * porta normal para trocar, o seletor do topo, só aparece para quem tem duas
 * unidades ou mais. Quem tinha uma unidade só nunca saía desse modo, e toda
 * tela que recusa "Todas as unidades" de propósito virava beco sem saída para
 * essa pessoa. Em 15/09/2026 um administrador da Rio Claro ESC não conseguiu
 * baixar a planilha do Funil: a tela pedia para escolher uma unidade no alto, e
 * lá não havia seletor. Medido na produção no mesmo dia: 17 contas de
 * administrador com uma unidade só, e nenhuma planilha baixada na rede desde
 * que o Exportar entrou no ar, em 02/09.
 *
 * "Unidade visível" é a lista que o seletor do topo usa: os vínculos da pessoa,
 * sem unidade desativada (o `tenants` do `TenantContext`).
 *
 * ⚠️ COM UMA UNIDADE SÓ, NADA VIRA PREFERÊNCIA GUARDADA. Não há escolha a
 * lembrar: nem a unidade única, nem o link, nem a ESC recém-criada são
 * gravados. Gravar inventaria uma preferência que a pessoa nunca manifestou, e
 * quem ganhasse uma segunda unidade abriria preso na primeira, em vez de abrir
 * como qualquer pessoa com várias unidades que nunca escolheu: em "Todas as
 * unidades".
 *
 * A regra é pura (sem React, sem `window`, sem `localStorage`) e mora só aqui.
 * O `TenantContext` lê o endereço e a memória, pergunta a esta regra e aplica a
 * resposta onde o escopo é definido: carga inicial, efeito de retaguarda,
 * recarga da lista de unidades e cada pedido de troca.
 */

/** O valor de `selectedScope` que significa "Todas as unidades". */
export const TODAS_AS_UNIDADES = "all"

export interface EscopoDecidido {
  /** O que vai para `selectedScope`: o id de uma unidade, ou "all". */
  escopo: string
  /**
   * A unidade do escopo, para `currentTenant`. `null` em "Todas as unidades" e
   * quando o pedido não casa com nenhuma unidade da lista.
   */
  unidade: Tenant | null
  /** Se a decisão vira a preferência guardada para a próxima visita. */
  gravar: boolean
}

/** A unidade que a invariante impõe: existe só com exatamente uma unidade visível. */
export function unidadeUnica(unidades: Tenant[]): Tenant | null {
  return unidades.length === 1 ? unidades[0] : null
}

/**
 * O escopo ao abrir o sistema, nesta ordem:
 *
 * 1. a unidade pedida no endereço (`?unidade=`), quando a pessoa alcança;
 * 2. a unidade guardada da última visita, quando ainda é válida;
 * 3. exatamente uma unidade visível: essa unidade;
 * 4. nos demais casos, "Todas as unidades".
 *
 * `pedidaNoEndereco` chega resolvida por `unidadeDoEndereco` (id ou apelido) e
 * é conferida de novo contra a lista: unidade que a pessoa não alcança não
 * decide nada. `guardada` é o valor cru da memória, que pode ser "all", um id
 * que deixou de valer ou nada.
 */
export function escopoAoAbrir(
  unidades: Tenant[],
  pedidaNoEndereco: Tenant | null,
  guardada: string | null,
): EscopoDecidido {
  const unica = unidadeUnica(unidades)

  const doEndereco = pedidaNoEndereco
    ? (unidades.find((u) => u.id === pedidaNoEndereco.id) ?? null)
    : null
  if (doEndereco) {
    // O link é escolha de quem clicou e fica guardado: navegar para outra tela
    // depois não pode devolver a pessoa à unidade anterior no meio da leitura.
    // Com uma unidade só não existe unidade anterior, e nada é gravado.
    return { escopo: doEndereco.id, unidade: doEndereco, gravar: !unica }
  }

  const daMemoria =
    guardada && guardada !== TODAS_AS_UNIDADES
      ? (unidades.find((u) => u.id === guardada) ?? null)
      : null
  // Já está guardada: gravar de novo não mudaria nada.
  if (daMemoria) return { escopo: daMemoria.id, unidade: daMemoria, gravar: false }

  if (unica) return { escopo: unica.id, unidade: unica, gravar: false }

  return { escopo: TODAS_AS_UNIDADES, unidade: null, gravar: false }
}

/**
 * Um pedido de troca de escopo: o seletor do topo, os botões "Selecione uma
 * unidade" das telas, a ESC recém-criada.
 *
 * Com uma unidade visível só, a invariante vence qualquer pedido (inclusive o
 * de "Todas as unidades") e nada é gravado. Com zero unidades, ou com duas ou
 * mais, o pedido vale como sempre valeu: o escopo é o pedido, a unidade é a que
 * casa com ele, e a escolha fica guardada.
 */
export function escopoPedido(pedido: string, unidades: Tenant[]): EscopoDecidido {
  const unica = unidadeUnica(unidades)
  if (unica) return { escopo: unica.id, unidade: unica, gravar: false }

  const unidade =
    pedido === TODAS_AS_UNIDADES ? null : (unidades.find((u) => u.id === pedido) ?? null)
  return { escopo: pedido, unidade, gravar: true }
}
